import React, { useState } from 'react';
import { Col, Form, Row, Table } from 'antd';
import PropTypes from 'prop-types';

import { uuid } from '../../helpers/helpers';
import useToggle from '../../common/hooks/useToggle';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import Colors from '../../constants/Colors';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import SimpleFormModal from '../../common/modals/SimpleFormModal';

export default function BucketTemplateStatusTable({
  bucketTemplateStatuses,
  onBucketTemplateStatusChange,
}) {
  const [addPayload, setAddPayload] = useState({});
  const [editData, setEditData] = useState({});

  const {
    toggle: toggleAddStatusModal,
    isToggled: addStatusModalVisible,
  } = useToggle();

  const closeModal = () => {
    toggleAddStatusModal();
    setAddPayload({});
  };

  const onFormChange = (property, newValue) => {
    setAddPayload({
      ...addPayload,
      [property]: newValue,
    });
  };

  const onSaveStatus = async () => {
    const { title } = addPayload;
    const trimmedTitle = title?.trimStart();
    const id = uuid();

    onBucketTemplateStatusChange([
      ...bucketTemplateStatuses,
      {
        id,
        status: trimmedTitle,
        isNew: true,
      },
    ]);
    closeModal();
  };

  const onNameChange = (editId) => (e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    if (editId) {
      const newEditData = { ...editData };
      newEditData[editId].status = value;
      setEditData(newEditData);
    }
  };

  const isInputClean = (record) => {
    const isEdit = record.id in editData;

    const {
      [record.id]: {
        status: editTitle,
      } = {},
    } = editData;

    const { status: existingTitle } = record;
    return isEdit && editTitle === existingTitle;
  };

  const onEditStarted = (editId) => {
    const newEditData = { ...editData };
    const existing = bucketTemplateStatuses.find((status) => status.id === editId);
    if (!existing) return;

    newEditData[editId] = { ...existing };
    setEditData(newEditData);
  };

  const onEditCancel = (editId) => {
    const newEditData = { ...editData };
    delete newEditData[editId];
    setEditData(newEditData);
  };

  const onSaveEdit = async (editId) => {
    const {
      [editId]: {
        status: title,
      } = {},
    } = editData;

    onBucketTemplateStatusChange(bucketTemplateStatuses.map((status) => (
      status.id === editId ? { ...status, status: title } : status
    )));
    const newEditData = { ...editData };
    delete newEditData[editId];
    setEditData(newEditData);
  };

  const onDeleteClicked = ({ id, status: title }) => {
    CustomConfirmModal({
      title: `Delete ${title} status?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk: () => onBucketTemplateStatusChange(
        bucketTemplateStatuses.filter((status) => status.id !== id)
      ),
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'status',
      render: (text, record) => {
        const isDirty = !isInputClean(record);
        const isEdit = record.id in editData;

        const {
          [record.id]: {
            status: editTitle,
          } = {},
        } = editData;
        if (isEdit) {
          return (
            <Row align="middle">
              <OnTraccrTextInput
                key={record.id}
                value={editTitle}
                onChange={onNameChange(record.id)}
                style={isDirty ? {} : { borderColor: 'red' }}
              />
            </Row>
          );
        }
        return text;
      },
    },
    {
      dataIndex: 'edit',
      align: 'edit',
      width: 60,
      render: (_, record) => {
        const { id } = record || {};
        const isEdit = id in editData;
        const icon = isEdit ? 'close' : 'edit';
        return (
          <BorderlessButton
            key={id}
            title=""
            icon={icon}
            color={Colors.ONTRACCR_BLACK}
            onClick={() => {
              if (isEdit) {
                onEditCancel(id);
              } else {
                onEditStarted(id);
              }
            }}
            style={{
              paddingRight: 8,
              paddingLeft: 0,
            }}
          />
        );
      },
    },
    {
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      width: 60,
      render: (_, record) => {
        const { id } = record || {};
        const isEdit = id in editData;
        const icon = isEdit ? 'check' : 'delete';
        const color = isEdit ? Colors.ONTRACCR_BLACK : Colors.ONTRACCR_RED;

        return !isInputClean(record)
          ? (
            <BorderlessButton
              title=""
              icon={icon}
              color={color}
              onClick={() => {
                if (isEdit) {
                  onSaveEdit(id);
                } else {
                  onDeleteClicked(record);
                }
              }}
              style={{
                paddingRight: 8,
                paddingLeft: 0,
              }}
            />
          ) : null;
      },
    },
  ];

  return (
    <>
      {/* Hidden form item that we will attach the statuses to */}
      <Form.Item name="statuses" style={{ display: 'none' }} />
      <Row justify="space-between">
        <Col>
          Statuses
        </Col>
        <Col>
          <OnTraccrButton type="primary" title="Add Status" onClick={toggleAddStatusModal} />
        </Col>
      </Row>
      <Table
        dataSource={bucketTemplateStatuses}
        columns={columns}
        size="small"
        pagination={false}
        style={{ marginTop: 10, marginBottom: 10}}
      />
      <SimpleFormModal
        title="Add Status"
        visible={addStatusModalVisible}
        onSave={onSaveStatus}
        onClose={closeModal}
      >
        <div style={{ width: '100%', display: 'inline-flex' }}>
          <OnTraccrTextInput
            onChange={({ target: { value: newVal } }) => onFormChange('title', newVal)}
            value={addPayload.title}
            placeholder="Enter Status Name"
          />
        </div>
      </SimpleFormModal>
    </>
  );
}

BucketTemplateStatusTable.propTypes = {
  bucketTemplateStatuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    isNew: PropTypes.bool.isRequired,
  })).isRequired,
  onBucketTemplateStatusChange: PropTypes.func.isRequired,
};
